// Copyright (c) Docugami, Inc. All rights reserved.

import { createSelector } from "@reduxjs/toolkit";
import type { AppState } from "store";
import { dgmlSlice, selectAllChunks } from "./dgmlSlice";
import { TokensTextParser } from "utils/TokensTextParser";
import type { LabelSchema } from "store/frontend/frontendSlice";
import { createFolderId } from "components/file_management/file_helpers";
import { DgmlDom } from "utils/dgmldom";
import { selectLabelState } from "store/frontend/selectors";
import { createMappedTokensForEdits } from "./dgmlUtils";

// Inline selector to avoid circular dependency with frontendSlice
// This is equivalent to labelEntityAdapter.getSelectors().selectAll
const selectAllLabelsFromState = (labelState: { entities: Record<string, LabelSchema | undefined> }) =>
    Object.values(labelState.entities).filter((x): x is LabelSchema => !!x);

export const selectTokens = createSelector(
    [
        (state: AppState, docsetId: string, fileId: string) => dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: docsetId, fileId: fileId })(state)?.data,
        (state: AppState, docsetId: string, fileId: string) => selectVersionedTokensData(state, docsetId, fileId),
        (state: AppState, docsetId: string, fileId: string) => fileId, // Ensure that different file IDs break caching
    ],
    (dgmlData, tokensData) => {
        if (!tokensData?.value || !dgmlData) {
            return undefined;
        }

        const parser: TokensTextParser = new TokensTextParser(tokensData.value);
        const tokens = parser.tokens;
        if (!!dgmlData.edits.length) {
            const currentTokensTextVersion = Number.parseInt(tokensData.version);
            for (const edit of dgmlData.edits.filter(x => x.streamVersion > currentTokensTextVersion)) {
                const removedTokens = [...edit.removedTokens].sort((a, b) => b.index - a.index);
                for (const removed of removedTokens) {
                    tokens.splice(removed.index, 1);
                }

                for (const added of edit.addedTokens) {
                    tokens.splice(added.index, 0, {
                        ...added,
                        whitespaceTypes: TokensTextParser.getWhitespaceTypes(added.type, added.text)
                    });
                }
            }
        }

        return tokens;
    },
    {
        memoizeOptions: {
            equalityCheck: (a, b) => {
                if (!!a?.version?.textEventStreamVersion && !!b?.version?.textEventStreamVersion) {
                    return a.version.textEventStreamVersion === b.version.textEventStreamVersion;
                }
                else if (!!a?.version && !!b?.version) {
                    return a.version === b.version;
                }

                return a === b;
            },
            maxSize: 3
        }
    }
);

const selectVersionedTokensData = (state: AppState, docsetId: string, fileId: string) => {
    const version = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: docsetId, fileId: fileId })(state)?.data?.version;
    if (!version) {
        return;
    }

    return dgmlSlice.endpoints.getTokens.select({ docsetId: docsetId, fileId: fileId, version: version.textEventStreamVersion.toString() })(state)?.data;
}

// Re-export for backwards compatibility (moved to dgmlUtils.ts to break circular dependency)
export { createMappedTokensForEdits } from "./dgmlUtils";

export const selectMappedTokens = createSelector(
    [
        (state: AppState, docsetId: string, fileId: string) => dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: docsetId, fileId: fileId })(state)?.data?.edits
    ],
    createMappedTokensForEdits,
    {
        memoizeOptions: {
            maxSize: 3
        }
    }
);

export const selectDgmlNodesByFolderId = (state: AppState, folderId: string) => {
    const fid = folderIdSelector(folderId);
    return !!fid ? dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(state)?.data : undefined;
}

export const selectSpansIndexedByLabel = createSelector(
    [
        (state: AppState, docsetId: string, fileId: string) => dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: docsetId, fileId: fileId })(state)?.data,
    ],
    (data): Record<string, string[]> => {
        const result: Record<string, string[]> = {}
        if (!!data) {
            selectAllChunks(data.chunks).forEach(x => {
                for (const label of x.labels) {
                    const spans = result[label.value] = (result[label.value] ?? []);
                    spans.push(x.spanId);
                }
            })
        }

        return result;
    },
    {
        memoizeOptions: {
            maxSize: 2
        }
    }
);

export const dgmlNodesSelector = createSelector(
    [
        (docsetId: string, fileId: string) => docsetId,
        (docsetId: string, fileId: string) => fileId,
    ],
    (docsetId, fileId) => dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: docsetId, fileId: fileId })
)

export const selectIsDgmlInitialized = createSelector(
    [
        (state: AppState, docsetId: string, fileId: string) => !!dgmlNodesSelector(docsetId, fileId)(state)?.data,
        (state: AppState, docsetId: string, fileId: string) => !!selectVersionedTokensData(state, docsetId, fileId),
        (state: AppState, docsetId: string, fileId: string) => !!selectLabelState(docsetId)(state)?.data,
        (state: AppState, docsetId: string, fileId: string) => fileId // Ensure that different file IDs break caching
    ],
    (chunksReady, tokensReady, labelsReady): boolean => {
        return chunksReady && tokensReady && labelsReady
    }
);

export const folderIdSelector = createSelector(
    [
        (folderId: string | null | undefined) => folderId,
    ],
    (folderId) => !!folderId ? createFolderId(folderId) : undefined,
    {
        memoizeOptions: {
            maxSize: 50
        }
    }
);

export const pagesInfoSelector = createSelector([
    (docsetId: string, fileId: string, version: string) => docsetId,
    (docsetId: string, fileId: string, version: string) => fileId,
    (docsetId: string, fileId: string, version: string) => version
],
    (docsetId, fileId, version) => dgmlSlice.endpoints.getPagesInfo.select({ docsetId: docsetId, fileId: fileId, version: version })
);

export const pageTokenLocationsSelector = createSelector([
    (docsetId: string, fileId: string, page: number, version: string) => docsetId,
    (docsetId: string, fileId: string, page: number, version: string) => fileId,
    (docsetId: string, fileId: string, page: number, version: string) => page,
    (docsetId: string, fileId: string, page: number, version: string) => version
],
    (docsetId, fileId, page, version) => dgmlSlice.endpoints.getTokenLocations.select({ docsetId: docsetId, fileId: fileId, page: page, version: version }),
    {
        memoizeOptions: {
            maxSize: 50
        }
    }
);

export const domSelector = createSelector(
    [
        (state: AppState, docsetId: string, fileId: string) => dgmlSlice.endpoints.getDgml.select({ docsetId: docsetId, fileId: fileId })(state).data,
        (state: AppState, docsetId: string, fileId: string) => fileId // Ensure that different file IDs break caching
    ],
    (dgmlState): DgmlDom | undefined => {
        if (!dgmlState) {
            return;
        }

        return new DgmlDom(dgmlState.value);
    },
    {
        memoizeOptions: {
            equalityCheck: (a, b) => {
                if (!!a?.version && !!b?.version) {
                    return a.version === b.version;
                }

                return a === b;
            }
        }
    }
)

export const selectDocumentReady = createSelector(
    [
        (state: AppState, documentId: string, isPreview?: boolean) => state.documents.indexed[documentId],
        (state: AppState, documentId: string, isPreview?: boolean) => selectIsDgmlInitialized(state, folderIdSelector(documentId)?.clusterId ?? '', folderIdSelector(documentId)?.fileId ?? ''),
        (state: AppState, documentId: string, isPreview?: boolean) => isPreview
    ],
    (document, isDgmlInitialized, isPreview) => {
        if (!document?.clusterDocument) {
            return false;
        }

        const shouldHaveDgml = !isPreview || !!document.clusterDocument.hasCpml;
        if (shouldHaveDgml && !isDgmlInitialized) {
            return false;
        }

        return true;
    }
)

export const selectDocsetLabels = createSelector([
    (state: AppState, docsetId: string) => selectLabelState(docsetId)(state)?.data,
],
    (labelState) => !!labelState ? selectAllLabelsFromState(labelState).filter(label => !label.isComplexType).map(label => label.label!) : []
);

export const selectDocsetComplexTypeLabelSchemas = createSelector([
    (state: AppState, docsetId: string) => selectLabelState(docsetId)(state)?.data,
],
    (labelState) => !!labelState ? selectAllLabelsFromState(labelState).filter(label => label.isComplexType) : []
);

export const selectDocsetLabelSchemas = createSelector([
    (state: AppState, docsetId: string) => selectLabelState(docsetId)(state)?.data,
],
    (labelState) => !!labelState ? selectAllLabelsFromState(labelState) : []
);

export const createSelectLabelId = () => createSelector([
    (state: AppState, docsetId: string) => selectLabelState(docsetId)(state)?.data,
    (state: AppState, docsetId: string, label: string) => label,
],
    (labelState, label) => !!labelState ? selectAllLabelsFromState(labelState).find(x => x.label === label)?.id : ''
);

export const createSelectLabelByIdOrValue = () => createSelector([
    (state: AppState, docsetId: string) => selectLabelState(docsetId)(state)?.data,
    (state: AppState, docsetId: string, label: string) => label,
],
    (labelState, label): LabelSchema | undefined => {
        if (!labelState) {
            return;
        }

        return labelState.entities[label] ?? selectAllLabelsFromState(labelState).find(x => x.label === label);
    }
);
